/**
 * Rehydration Provider - React integration for strict F5 pipeline
 * 
 * Wraps the app and blocks rendering of protected routes until
 * the rehydration pipeline completes (token refresh + socket + game:state).
 */

'use client';

import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { initializeRehydration, RehydrationManager, type RehydrationState } from '@fog-chess/core';

interface RehydrationContextValue {
  state: RehydrationState;
  isReady: boolean;
}

const RehydrationContext = createContext<RehydrationContextValue | null>(null);

/**
 * Hook to access rehydration state
 */
export function useRehydration(): RehydrationContextValue {
  const ctx = useContext(RehydrationContext);
  if (!ctx) {
    throw new Error('useRehydration must be used within RehydrationProvider');
  }
  return ctx;
}

/**
 * RehydrationProvider - blocks UI until strict pipeline completes
 * 
 * Usage: Wrap your <App /> or specific route branches
 */
interface RehydrationProviderProps {
  children: ReactNode;
  /** If true, show a loading UI while pipeline executes */
  showLoading?: boolean;
}

export function RehydrationProvider({ 
  children, 
  showLoading = true 
}: RehydrationProviderProps) {
  const [state, setState] = useState<RehydrationState>(() => 
    RehydrationManager.getInstance().getState()
  );
  const [initialized, setInitialized] = useState(false);

  const value = useMemo(() => ({
    state,
    isReady: state.status === 'ready',
  }), [state]);

  // Subscribe to manager updates
  useEffect(() => {
    const manager = RehydrationManager.getInstance();
    const unsubscribe = manager.subscribe(setState);
    return unsubscribe;
  }, []);

  // Run pipeline once on mount
  useEffect(() => {
    let mounted = true;
    
    const runPipeline = async () => {
      try {
        await initializeRehydration();
        if (mounted) {
          setInitialized(true);
        }
      } catch (err) {
        console.error('[RehydrationProvider] Pipeline failed:', err);
        if (mounted) {
          setInitialized(true); // Allow fallback to login
        }
      }
    };

    runPipeline();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Show loading UI while pipeline runs
  if (showLoading && !initialized) {
    return (
      <RehydrationContext.Provider value={value}>
        <RehydrationLoadingScreen state={state} />
      </RehydrationContext.Provider>
    );
  }

  return (
    <RehydrationContext.Provider value={value}>
      {children}
    </RehydrationContext.Provider>
  );
}

/**
 * Loading screen shown during rehydration
 */
function RehydrationLoadingScreen({ state }: { state: RehydrationState }) {
  const messages: Record<string, string> = {
    authenticating: 'Restoring session...',
    connecting: 'Connecting to game server...',
    awaiting_game_state: `Rejoining match ${state.matchId?.slice(0, 8) || ''}...`,
    ready: 'Ready!',
    error: state.error || 'Failed to restore session',
  };

  const message = messages[state.status] || 'Loading...';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-6">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mx-auto">
          <svg className="h-8 w-8 text-primary animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
          </svg>
        </div>
        
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-foreground">
            {message}
          </h2>
          
          {state.status === 'error' && (
            <p className="text-sm text-destructive">
              {state.error}
            </p>
          )}
          
          {(state.status === 'authenticating' || state.status === 'connecting' || state.status === 'awaiting_game_state') && (
            <div className="w-64 mx-auto">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300 ease-out"
                  style={{ width: `${state.progress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {state.progress}%
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}